import _ from 'lodash';
import BaseAccountController from '../../lib/controllers/BaseAccountController';

declare var LtFramework: lt.Static;

export default class ManagePhoneNumbersController extends BaseAccountController {
	public static $inject: string[] = ['$scope', 'UserResource', 'UserPhoneNumberResource'];

	public updateSuccessful: boolean;
	public user: MyAccountApi.IUser;
	public phoneNumbers: AccountPhoneNumber[];
	public newPhone: AccountPhoneNumber | null;
	public generalMessage: string;

	public deleteDialogPhoneNumber: AccountPhoneNumber;
	public deleteDialogLoading: boolean;

	private readonly User: MyAccountApi.IUserResource;
	private readonly PhoneNumber: MyAccountApi.IUserPhoneNumberResource;

	constructor($scope: ng.IScope, User: MyAccountApi.IUserResource, PhoneNumbers: MyAccountApi.IUserPhoneNumberResource) {
		super($scope);

		this.User = User;
		this.PhoneNumber = PhoneNumbers;
	}

	public deletePhone(phoneNumber: AccountPhoneNumber): void {
		this.generalMessage = '';

		this.deleteDialogPhoneNumber = phoneNumber;
		this.deleteDialogLoading = false;
		$('#deleteModal').modal();
	}

	public deletePhoneConfirmed(): void {
		this.deleteDialogLoading = true;

		this.deleteDialogPhoneNumber.delete(() => {
			const index = this.phoneNumbers.indexOf(this.deleteDialogPhoneNumber);
			this.phoneNumbers.splice(index, 1);
			this.generalMessage = `You’ve successfully deleted "${this.deleteDialogPhoneNumber.label}".`;

			$('#deleteModal').modal('hide');

			// if the user now has no phone numbers, automatically show Add New Phone Number
			if (this.phoneNumbers.length === 0) {
				this.addNewPhone();
			}
		}, () => {
			$('#deleteModal').modal('hide');
		});
	}

	public addNewPhone(): void {
		this.newPhone = new AccountPhoneNumber(new this.PhoneNumber());
	}

	public validateLabel(phoneNumber: AccountPhoneNumber, ctrl: ng.INgModelController): void {
		ctrl.$setValidity('notHome', !phoneNumber.label || phoneNumber.label.trim().toLowerCase() !== 'Home');

		ctrl.$setValidity('unique', !phoneNumber.label || !_.find(this.phoneNumbers, (tel) => {
			return tel.label === phoneNumber.label
				&& tel.contactMethodId !== phoneNumber.contactMethodId;
		}));
	}

	public cancelAddNew(): void {
		this.newPhone = null;
	}

	public saveNewPhone(form: ng.IFormController, phoneNumber: AccountPhoneNumber): void {
		const updatePhoneNumberError = this.canUpdatePhone(form, phoneNumber);
		this.generalMessage = '';

		if (!updatePhoneNumberError) {
			phoneNumber.create((createdPhoneNumber: AccountPhoneNumber) => {
				this.phoneNumbers.push(createdPhoneNumber);
				this.newPhone = null;
			});
		} else {
			phoneNumber.successMessage = '';
			phoneNumber.errorMessage = updatePhoneNumberError;
		}
	}

	public canUpdatePhone(form: ng.IFormController, phoneNumber: AccountPhoneNumber): string | undefined {
		let canUpdateError: string | undefined;

		if (!form.$valid) {
			canUpdateError = 'Sorry, this field contains errors.';
		}

		return canUpdateError;
	}

	public updatePhone(form: ng.IFormController, phoneNumber: AccountPhoneNumber): string | undefined  {
		const canUpdateError = this.canUpdatePhone(form, phoneNumber);

		this.generalMessage = '';

		if (!canUpdateError) {
			phoneNumber.save();
		} else {
			phoneNumber.successMessage = '';
			phoneNumber.errorMessage = canUpdateError;
		}

		return canUpdateError;
	}

	public hasMembershipPhoneNumbers(): boolean {
		return this.phoneNumbers && this.phoneNumbers.filter((num: AccountPhoneNumber) => {
			return num.isMembershipPhone;
		}).length > 0;
	}

	public hasAdditionalPhoneNumbers(): boolean {
		return this.phoneNumbers && this.phoneNumbers.filter((num: AccountPhoneNumber) => {
			return !num.isMembershipPhone;
		}).length > 0;
	}

	/**
	 * Triggered whenever SSO ID is available/changed
	 */
	protected loadData(): void {
		// load My Account user details
		this.user = this.User.get();

		// load phone numbers
		this.PhoneNumber.query((phoneNumbers: MyAccountApi.IUserPhoneNumber[]) => {
			this.phoneNumbers = phoneNumbers.map((phoneNumber: MyAccountApi.IUserPhoneNumber) => {
				return new AccountPhoneNumber(phoneNumber);
			});

			// if the user has no phone numbers, automatically show Add New Phone Number
			if (this.phoneNumbers.length === 0) {
				this.addNewPhone();
			}
		}, () => {
			this.phoneNumbers = [];
		});
	}

	/**
	 * Triggered whenever profile data is available/changed
	 */
	protected profileDataLoaded(): void {
		return;
	}
}

// tslint:disable-next-line: max-classes-per-file
export class AccountPhoneNumber {
	public contactMethodId: number;
	public label: string;
	public numberText: string;
	public isMembershipPhone: boolean;
	public isTextNumber: boolean;
	public doNotDelete: boolean;

	public successMessage: string;
	public errorMessage: string;

	private readonly phoneNumber: MyAccountApi.IUserPhoneNumber;

	constructor(phoneNumber: MyAccountApi.IUserPhoneNumber) {
		this.phoneNumber = phoneNumber;

		// telephone will be null in the case of a new number
		if (!this.phoneNumber.telephone) {
			this.phoneNumber.telephone = {
				areaCode: '',
				prefix: '',
				suffix: '',
				valid: true
			};
		}

		this.contactMethodId = this.phoneNumber.contactMethodId;
		this.label = this.phoneNumber.telephoneLabel;
		this.numberText = `${this.phoneNumber.telephone.areaCode}${this.phoneNumber.telephone.prefix}${this.phoneNumber.telephone.suffix}`;
		this.isMembershipPhone = !!this.phoneNumber.isMembershipPhone;
		this.isTextNumber = !!this.phoneNumber.isTextNumber;
		this.doNotDelete = !!this.phoneNumber.doNotDelete;
	}

	public canEdit(): boolean {
		return this.phoneNumber.canEdit;
	}

	// tslint:disable-next-line: ban-types
	public create(success?: Function): void {
		this.update();

		this.successMessage = '';
		this.errorMessage = '';

		this.phoneNumber.$create((createdPhoneNumber: MyAccountApi.IUserPhoneNumber) => {
			this.successMessage = 'You’ve successfully added your phone number(s).';

			if (success) {
				const newNum: AccountPhoneNumber = new AccountPhoneNumber(createdPhoneNumber);
				newNum.successMessage = this.successMessage;
				success(newNum);
			}
		}, (err: MyAccountApi.IServiceFailureResponse) => {
			this.errorMessage = '';

			err.data.validationErrors.forEach((err2) => {
				this.errorMessage += `${err2.message}\n`;
			});
		});
	}

	// tslint:disable-next-line: ban-types
	public save(success?: Function): void {
		this.update();

		this.successMessage = '';
		this.errorMessage = '';

		this.phoneNumber.$save((updatedPhoneNumber: MyAccountApi.IUserPhoneNumber) => {
			this.successMessage = 'You’ve successfully updated your phone number(s).';

			// if the label is changed it's a create/delete on the backend, so contactMethodId changes
			this.contactMethodId = updatedPhoneNumber.contactMethodId;

			if (success) {
				success(new AccountPhoneNumber(updatedPhoneNumber));
			}
		}, (err: MyAccountApi.IServiceFailureResponse) => {
			this.errorMessage = '';

			err.data.validationErrors.forEach((err2) => {
				this.errorMessage += `${err2.message}\n`;
			});
		});
	}

	// tslint:disable-next-line: ban-types
	public delete(success?: Function, error?: Function): void {
		this.successMessage = '';
		this.errorMessage = '';

		this.phoneNumber.$delete(success, (err: string) => {
			this.errorMessage = err;

			if (error) {
				error(err);
			}
		});
	}

	private update(): void {
		this.phoneNumber.telephoneLabel = this.label;
		this.phoneNumber.telephone.areaCode = this.numberText.substring(0, 3);
		this.phoneNumber.telephone.prefix = this.numberText.substring(3, 6);
		this.phoneNumber.telephone.suffix = this.numberText.substring(6);
	}
}
